/**
 * AGNT, Inc. root-route (`/`) integration.
 *
 * AGNT shares `/` with the listings homepage (`pages/index`), which dispatches
 * here for the agntInc tenant. Owning the SSR fetch, the render chrome, and the
 * props contract in this module keeps `pages/index` free of AGNT detail — it
 * only needs the two tenant guards that delegate here.
 */

import AgntHomePage from 'components/content-pages/agnt-inc/home';
import AgntIncLayout from 'components/layout/AgntInc';
import newsCardsFromPressReleases from 'components/content-pages/agnt-inc/home/news-from-press-releases';
import { fetchLatestPressReleases } from 'components/content-pages/agnt-inc/press-releases/fetch';
import { fetchAgntStockData } from 'utils/content-pages/agnt-yahoo-quote';
import { getContentPageUrlForTenant } from '@tenants/utils/hosts';
import isUserAgentCrawler from 'utils/crawler-agent';
import { ROOT_ROUTE } from 'components/dynamic-page/route-matchers';

import type { TenantTypes } from '@tenants/manager';
import type { GetServerSidePropsContext, GetServerSidePropsResult } from 'next';
import type { YoastHeadJson } from 'utils/content-pages/yoast';
import type { NewsCard } from './content';
import type { RootPageProps } from 'pages';

/** AGNT-specific slice of the shared `RootPageProps` consumed by the render. */
export interface AgntHomeRootProps {
  /** Yoast SEO for the agntInc home page (null when unavailable). */
  agntSeo?: YoastHeadJson | null;
  /** Latest press releases (as News cards) for the agntInc home page. */
  agntNewsCards?: NewsCard[];
  /**
   * Live AGNT stock price, preformatted (e.g. `$6.82`), for the home page's
   * Stock Information row — same Yahoo source as the /stock-info page. `null`
   * when the quote is unavailable (the row falls back to its static copy).
   */
  agntStock?: string | null;
}

/**
 * Server-side props for the AGNT home. AGNT has no listings home — the root
 * serves the investor-relations landing, so we skip the listings/header fetch
 * and only fetch the page's Yoast SEO + latest press releases (both degrade
 * gracefully — null SEO / empty cards — on miss).
 */
export async function getAgntHomeServerSideProps(
  context: GetServerSidePropsContext,
  tenant: TenantTypes
): Promise<GetServerSidePropsResult<RootPageProps>> {
  const wpOrigin = getContentPageUrlForTenant(tenant.id, 'ALL');
  const [pressReleases, agntStockData] = await Promise.all([
    fetchLatestPressReleases(wpOrigin, 3),
    fetchAgntStockData('AGNT'),
  ]);
  const agntSeo = null;
  return {
    props: {
      isCrawler: isUserAgentCrawler(context.req.headers),
      featuredHomesMlsNums: [],
      isNotificationBannerHidden: true,
      availableHomeListingsData: [],
      availableHomeListingsMeta: 0,
      availableHomeListingsLocation: '',
      internalLinksData: [],
      tenantId: tenant.id,
      routeName: ROOT_ROUTE,
      agntSeo,
      agntNewsCards: newsCardsFromPressReleases(pressReleases),
      agntStock: agntStockData ? `$${agntStockData.price.toFixed(2)}` : null,
    },
  };
}

/** Renders the AGNT investor-relations landing within its tenant layout. */
export default function AgntHomeRoot({ agntSeo = null, agntNewsCards, agntStock = null }: AgntHomeRootProps) {
  return (
    <AgntIncLayout>
      <AgntHomePage seo={agntSeo} newsCards={agntNewsCards} stockPrice={agntStock} />
    </AgntIncLayout>
  );
}
